import clsx from "classnames";
import { useState, useCallback, useMemo } from "react";
import { useStyles } from "./Directory.style";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { DirectoryTab, LIST_TAB_DIRECTORY } from "../../../constants/services";
import {
  setIsShowService,
  triggerDelayWithTimeout,
} from "../../../store/slices/popup";
import AppBar from "../AppBar/AppBar";
import { Icon } from "../../core/Icon/Icon";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { TabsList } from "../TabsList";
import { DirectoryRow } from "./DirectoryRow";
import { useDirectoryData } from "../../../hooks/useDirectoryData";

export type ImageSet = { src1x: string; src2x?: string; src3x?: string };

const Directory: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { favouriteGames, recentlyPlayed, listService } = useDirectoryData();
  const [activeTab, setActiveTab] = useState(LIST_TAB_DIRECTORY[0]);
  const { isShowService, isPopupDelayActive } = useAppSelector(
    (state) => state.popup,
  );

  const onCloseServicePopup = useCallback(() => {
    dispatch(triggerDelayWithTimeout(250));
    dispatch(setIsShowService({ isShowService: false }));
  }, [dispatch]);

  const onChangeTab = useCallback((tab: string) => {
    setActiveTab(tab as DirectoryTab);
  }, []);

  const sections = useMemo(
    () => [
      { id: DirectoryTab.MySubscriptions, title: "My Subscriptions", data: [] },
      {
        id: DirectoryTab.MyFavorites,
        title: "My Favorites",
        data: favouriteGames,
      },
      {
        id: DirectoryTab.RecentlyPlayed,
        title: "Recently Played",
        data: recentlyPlayed,
      },
      {
        id: DirectoryTab.GameServices,
        title: "Game Services",
        data: listService,
      },
    ],
    [favouriteGames, recentlyPlayed, listService],
  );

  const renderTabContent = (activeTab: string) => {
    if (activeTab === DirectoryTab.AllServices) {
      const visibleSections = sections.filter(
        (section) => section.data.length > 0,
      );
      return visibleSections.map((section, index) => (
        <div key={section.id} className={classes.rowAllTab}>
          <DirectoryRow items={section.data} title={section.title} />
          {index < visibleSections.length - 1 && (
            <div className={classes.divider} />
          )}
        </div>
      ));
    }

    const currentSection = sections.find((s) => s.id === activeTab);
    return (
      <DirectoryRow
        items={currentSection?.data || []}
        title={currentSection?.title || ""}
        className={classes.rowAllTab}
      />
    );
  };

  if (!isShowService && !isPopupDelayActive) return null;
  return (
    <div
      className={clsx(
        classes.root,
        isShowService ? classes.forwardsAnimate : classes.backwardsAnimate,
      )}
    >
      <AppBar
        leftItems={
          <Icon
            className={classes.icon}
            name="icon-linear-arrow-left-3"
            onClick={onCloseServicePopup}
            data-testid="settings-icon"
          />
        }
        centerItems={
          <Typography variant={TypographyVariant.BodyB4Medium}>
            Directory
          </Typography>
        }
      />
      <div className={classes.service}>
        <TabsList
          activeTab={activeTab}
          onChangeTab={onChangeTab}
          listTabs={LIST_TAB_DIRECTORY}
          className={classes.tabs}
        />
        {renderTabContent(activeTab)}
      </div>
    </div>
  );
};

export default Directory;
